import { useState, useEffect } from "react";
import "@/App.css";
import { content, BUSINESS, REVIEWS, LEGAL } from "@/data/content";
import {
  FileSignature,
  Stamp,
  FileText,
  Languages,
  MapPin,
  Phone,
  MessageCircle,
  Mail,
  ArrowRight,
  Menu,
  X,
  Check,
  Star,
  Clock,
  ShieldCheck,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = { FileSignature, Stamp, FileText, Languages };

const HERO_IMG =
  "https://images.unsplash.com/photo-1605308071366-cf1ea0bf34db?crop=entropy&cs=srgb&fm=jpg&q=80&w=900";

// Fire-and-forget WhatsApp click tracking
const trackWhatsApp = (source) => {
  try {
    const body = JSON.stringify({ type: "whatsapp_click", source });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/events`, new Blob([body], { type: "application/json" }));
    } else {
      axios.post(`${API}/events`, { type: "whatsapp_click", source }).catch(() => {});
    }
  } catch (e) {
    /* never block navigation */
  }
};

const makeWaLink = (message) =>
  `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(message)}`;

const Container = ({ children, className = "" }) => (
  <div className={`max-w-7xl mx-auto px-6 lg:px-8 ${className}`}>{children}</div>
);

const Overline = ({ children, light = false }) => (
  <div className="flex items-center gap-3">
    <span className="h-px w-8 bg-gold" />
    <p className={`text-xs font-bold uppercase tracking-[0.22em] ${light ? "text-gold-light" : "text-gold-dark"}`}>
      {children}
    </p>
  </div>
);

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

// ---------- Header ----------
const Header = ({ t, lang, setLang, waLink }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "why", label: t.nav.why },
    { id: "process", label: t.nav.process },
    { id: "faq", label: t.nav.faq },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-[0_1px_20px_rgba(11,31,58,0.05)]"
          : "bg-white/85 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <Container className="flex items-center justify-between h-[74px]">
        <button onClick={() => go("hero")} data-testid="brand-logo" className="flex items-center gap-3">
          <span className="flex items-center justify-center w-10 h-10 bg-navy text-gold font-heading font-bold text-lg rounded-sm border border-gold/30">TD</span>
          <span className="font-heading text-[17px] font-semibold tracking-tight text-navy leading-none text-left">
            Turkish Docs
            <span className="block text-[11px] font-medium uppercase tracking-[0.2em] text-gold-dark mt-0.5">London</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button key={l.id} data-testid={`nav-${l.id}`} onClick={() => go(l.id)}
              className="text-sm font-medium text-slate-600 hover:text-navy transition-colors">
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center border border-slate-300 rounded-sm overflow-hidden shadow-sm" data-testid="language-toggle">
            {["tr", "en"].map((code) => (
              <button key={code} data-testid={`lang-${code}`} onClick={() => setLang(code)}
                aria-pressed={lang === code}
                className={`px-2.5 sm:px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                  lang === code ? "bg-navy text-white" : "bg-white text-slate-500 hover:text-navy"
                }`}>
                {code}
              </button>
            ))}
          </div>

          <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp-btn"
            onClick={() => trackWhatsApp("header")}
            className="hidden sm:inline-flex items-center gap-2 bg-navy hover:bg-navy-950 text-white text-sm font-semibold px-4 py-2.5 rounded-sm transition-colors">
            <MessageCircle className="w-4 h-4 text-gold-light" strokeWidth={2} />
            {t.nav.cta}
          </a>

          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden p-2 text-navy">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </Container>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
          <Container className="py-4 flex flex-col gap-1">
            {links.map((l) => (
              <button key={l.id} onClick={() => go(l.id)}
                className="text-left py-3 text-base font-medium text-slate-700 hover:text-navy border-b border-slate-100">
                {l.label}
              </button>
            ))}
            <div className="flex items-center gap-3 pt-4">
              <div className="flex items-center border border-slate-200 rounded-sm overflow-hidden">
                {["tr", "en"].map((code) => (
                  <button key={code} onClick={() => setLang(code)}
                    className={`px-3 py-1.5 text-xs font-bold uppercase ${lang === code ? "bg-navy text-white" : "bg-white text-slate-500"}`}>
                    {code}
                  </button>
                ))}
              </div>
              <a href={waLink} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("mobile_menu")}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2.5 rounded-sm">
                <MessageCircle className="w-4 h-4 text-gold-light" /> {t.nav.cta}
              </a>
            </div>
          </Container>
        </div>
      )}
    </header>
  );
};

// ---------- Hero ----------
const Hero = ({ t, waLink }) => (
  <section id="hero" className="pt-[74px] bg-white" data-testid="hero-section">
    <Container className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 lg:py-24">
      <div className="order-2 lg:order-1">
        <Overline>{t.hero.overline}</Overline>
        <h1 className="font-heading mt-6 text-4xl sm:text-5xl lg:text-[3.5rem] tracking-tight font-semibold text-navy leading-[1.07]">
          {t.hero.title}
        </h1>
        <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">{t.hero.subtitle}</p>

        <div className="mt-9 flex flex-col sm:flex-row gap-3">
          <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn"
            onClick={() => trackWhatsApp("hero")}
            className="inline-flex items-center justify-center gap-2.5 bg-navy hover:bg-navy-950 text-white text-base font-semibold px-7 py-4 rounded-sm transition-colors shadow-[0_10px_30px_rgba(11,31,58,0.18)]">
            <MessageCircle className="w-5 h-5 text-gold-light" strokeWidth={2} />
            {t.hero.whatsapp}
          </a>
          <button onClick={() => scrollTo("services")} data-testid="hero-request-btn"
            className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-navy text-base font-semibold px-7 py-4 rounded-sm border border-navy/20 hover:border-gold transition-colors">
            {t.hero.request}
            <ArrowRight className="w-5 h-5 text-gold-dark" strokeWidth={2} />
          </button>
        </div>

        <div className="mt-10 flex items-center gap-2 border-t border-slate-200 pt-6 text-sm font-semibold text-navy">
          <MapPin className="w-4 h-4 text-gold-dark" strokeWidth={2} /> {t.hero.officeLabel}, {t.hero.officeCity}
        </div>
      </div>

      <div className="order-1 lg:order-2 relative">
        <div className="absolute -inset-3 border border-gold/40 rounded-sm hidden lg:block" />
        <div className="aspect-[4/5] lg:aspect-[5/6] overflow-hidden rounded-sm bg-slate-100 relative">
          <img src={HERO_IMG} alt={t.hero.imageAlt} width="900" height="1080" className="w-full h-full object-cover" loading="eager" decoding="async" fetchpriority="high" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent" />
        </div>
      </div>
    </Container>
  </section>
);

// ---------- Trust Bar ----------
const TrustBar = ({ t }) => (
  <section className="bg-navy border-y border-gold/20" data-testid="trust-bar">
    <Container className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
      {t.trustBar.map((item, i) => (
        <div key={i} data-testid={`trust-item-${i}`} className="flex items-center gap-2.5 px-4 py-5 justify-center text-center">
          <Check className="w-4 h-4 text-gold-light flex-shrink-0" strokeWidth={3} />
          <span className="text-xs sm:text-sm font-semibold text-white">{item}</span>
        </div>
      ))}
    </Container>
  </section>
);

// ---------- Why ----------
const Why = ({ t, waLink }) => (
  <section id="why" className="py-20 sm:py-24 lg:py-28 bg-white" data-testid="why-section">
    <Container className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <div>
        <Overline>{t.why.overline}</Overline>
        <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-navy">{t.why.title}</h2>
        <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-lg">{t.why.subtitle}</p>
        <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="why-whatsapp-btn" onClick={() => trackWhatsApp("why")}
          className="mt-8 inline-flex items-center gap-2.5 bg-gold hover:bg-gold-dark text-navy text-base font-bold px-7 py-3.5 rounded-sm transition-colors">
          <MessageCircle className="w-5 h-5" strokeWidth={2} />
          {t.hero.whatsapp}
        </a>
      </div>

      <ul className="space-y-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
        {t.why.items.map((item, i) => (
          <li key={i} data-testid={`why-item-${i}`} className="flex items-center gap-4 bg-white px-6 py-5 hover:bg-slate-50 transition-colors">
            <span className="flex items-center justify-center w-9 h-9 flex-shrink-0 rounded-full bg-gold/15 border border-gold/40">
              <Check className="w-5 h-5 text-gold-dark" strokeWidth={2.5} />
            </span>
            <span className="text-base font-medium text-navy">{item}</span>
          </li>
        ))}
      </ul>
    </Container>
  </section>
);

// ---------- Services ----------
const Services = ({ t }) => (
  <section id="services" className="py-20 sm:py-24 lg:py-32 bg-slate-50" data-testid="services-section">
    <Container>
      <div className="max-w-2xl">
        <Overline>{t.services.overline}</Overline>
        <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-navy">{t.services.title}</h2>
        <p className="mt-5 text-base sm:text-lg text-slate-600">{t.services.subtitle}</p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {t.services.items.map((item, i) => {
          const Icon = ICONS[item.icon];
          return (
            <div key={i} data-testid={`service-card-${i}`}
              className="group flex flex-col bg-white border border-slate-200 rounded-sm p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-gold hover:shadow-[0_18px_50px_rgba(11,31,58,0.08)] relative overflow-hidden">
              <span className="absolute top-0 left-0 h-1 w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              <span className="flex items-center justify-center w-12 h-12 bg-navy rounded-sm mb-6">
                <Icon className="w-6 h-6 text-gold-light" strokeWidth={1.5} />
              </span>
              <h3 className="font-heading text-lg font-semibold text-navy leading-snug">{item.title}</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{item.desc}</p>
              <a href={makeWaLink(item.whatsappMessage)} target="_blank" rel="noopener noreferrer"
                onClick={() => trackWhatsApp(`service_${i}`)} data-testid={`service-contact-${i}`}
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-navy group-hover:text-gold-dark hover:gap-2.5 transition-all">
                {t.services.contact}
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </a>
            </div>
          );
        })}
      </div>
    </Container>
  </section>
);

// ---------- Process ----------
const Process = ({ t }) => (
  <section id="process" className="py-20 sm:py-24 lg:py-32 bg-white" data-testid="process-section">
    <Container>
      <div className="max-w-2xl">
        <Overline>{t.process.overline}</Overline>
        <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-navy">{t.process.title}</h2>
        <p className="mt-5 text-base sm:text-lg text-slate-600">{t.process.subtitle}</p>
      </div>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {t.process.steps.map((step, i) => (
          <div key={i} data-testid={`process-step-${i}`} className="bg-slate-50 border border-slate-200 rounded-sm p-8 relative group hover:border-gold transition-colors">
            <div className="flex items-center justify-between">
              <span className="font-heading text-5xl font-semibold text-slate-200 group-hover:text-gold/50 transition-colors leading-none">
                {String(i + 1).padStart(2, "0")}
              </span>
              {i < t.process.steps.length - 1 && <ArrowRight className="w-5 h-5 text-gold-dark hidden lg:block" strokeWidth={2} />}
            </div>
            <h3 className="font-heading mt-6 text-lg font-semibold text-navy">{step.title}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.desc}</p>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

// ---------- Reviews (hidden when no reviews) ----------
const Reviews = ({ t }) => {
  if (!REVIEWS || REVIEWS.length === 0) return null;
  return (
    <section id="reviews" className="py-20 sm:py-24 lg:py-32 bg-slate-50" data-testid="reviews-section">
      <Container>
        <div className="max-w-2xl">
          <Overline>{t.reviews.overline}</Overline>
          <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-navy">{t.reviews.title}</h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600">{t.reviews.subtitle}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {REVIEWS.map((r, i) => (
            <div key={i} data-testid={`review-card-${i}`} className="bg-white border border-slate-200 rounded-sm p-8 flex flex-col">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className={`w-4 h-4 ${s < r.rating ? "text-gold fill-gold" : "text-slate-200 fill-slate-200"}`} />
                ))}
              </div>
              <p className="mt-5 text-base text-slate-700 leading-relaxed flex-1">“{r.text}”</p>
              <div className="mt-6 flex items-center gap-2 pt-5 border-t border-slate-100">
                <span className="font-semibold text-navy">{r.name}</span>
                <span className="inline-flex items-center gap-1 text-xs text-slate-400 ml-auto">
                  <ShieldCheck className="w-3.5 h-3.5 text-gold-dark" /> {t.reviews.verified}
                </span>
              </div>
            </div>
          ))}
        </div>

        {BUSINESS.googleReviewsLink && (
          <div className="mt-10">
            <a href={BUSINESS.googleReviewsLink} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold-dark transition-colors">
              {t.reviews.cta} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        )}
      </Container>
    </section>
  );
};

// ---------- SEO content ----------
const SeoContent = ({ t }) => (
  <section className="py-20 sm:py-24 bg-white" data-testid="seo-section">
    <Container className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <Overline>{t.seo.overline}</Overline>
        <h2 className="font-heading mt-5 text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold text-navy">{t.seo.title}</h2>
        <div className="mt-6 flex flex-wrap gap-2">
          {t.seo.tags.map((tag, i) => (
            <span key={i} className="inline-block text-xs font-semibold text-navy bg-slate-100 border border-slate-200 rounded-full px-3 py-1.5">{tag}</span>
          ))}
        </div>
      </div>
      <div className="lg:col-span-7 space-y-5">
        {t.seo.paragraphs.map((p, i) => (
          <p key={i} className="text-base text-slate-600 leading-relaxed">{p}</p>
        ))}
      </div>
    </Container>
  </section>
);

// ---------- Hours / Google Business Profile ----------
const Hours = ({ t, waLink }) => (
  <section className="py-20 sm:py-24 lg:py-28 bg-navy" data-testid="hours-section">
    <Container>
      <div className="max-w-2xl">
        <Overline light>{t.hours.overline}</Overline>
        <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-white">{t.hours.title}</h2>
        <p className="mt-5 text-base sm:text-lg text-slate-300">{t.hours.subtitle}</p>
      </div>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white/5 border border-white/10 rounded-sm p-8">
          <div className="flex items-center gap-2 text-gold-light"><Clock className="w-5 h-5" /><p className="text-xs font-bold uppercase tracking-[0.18em]">{t.hours.hoursTitle}</p></div>
          <ul className="mt-5 space-y-3">
            {t.hours.list.map((h, i) => (
              <li key={i} data-testid={`hours-row-${i}`} className="flex items-center justify-between border-b border-white/10 pb-3 last:border-0">
                <span className="text-sm font-medium text-white">{h.day}</span>
                <span className={`text-sm font-semibold ${h.open ? "text-gold-light" : "text-slate-500"}`}>{h.time}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-sm p-8">
          <div className="flex items-center gap-2 text-gold-light"><MapPin className="w-5 h-5" /><p className="text-xs font-bold uppercase tracking-[0.18em]">{t.hours.addressTitle}</p></div>
          <address className="mt-5 not-italic text-sm font-medium text-white leading-relaxed">
            {BUSINESS.address.line1}<br />{BUSINESS.address.line2}<br />
            {BUSINESS.address.line3}, {BUSINESS.address.line4}<br />{BUSINESS.address.line5}
          </address>
          <a href={BUSINESS.mapsLink} target="_blank" rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gold-light hover:text-white transition-colors">
            {t.hours.directions} <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="rounded-sm overflow-hidden border border-white/10 bg-white" data-testid="hours-map">
          <iframe title="Office map" src={BUSINESS.mapsEmbed} width="100%" height="100%" style={{ border: 0, display: "block", minHeight: "260px" }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </div>
      </div>
    </Container>
  </section>
);

// ---------- FAQ ----------
const Faq = ({ t }) => (
  <section id="faq" className="py-20 sm:py-24 lg:py-32 bg-slate-50" data-testid="faq-section">
    <Container>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Overline>{t.faq.overline}</Overline>
          <h2 className="font-heading mt-5 text-3xl sm:text-4xl tracking-tight font-semibold text-navy">{t.faq.title}</h2>
        </div>
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="w-full">
            {t.faq.items.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-slate-200">
                <AccordionTrigger className="text-left text-base sm:text-lg font-medium text-navy hover:text-gold-dark hover:no-underline py-5">{item.q}</AccordionTrigger>
                <AccordionContent className="text-base text-slate-600 leading-relaxed pb-5">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div data-testid="pricing-info" className="mt-8 border-l-2 border-gold bg-white px-5 py-4 rounded-sm">
            <p className="text-sm text-slate-600 leading-relaxed">{t.pricing.text}</p>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

// ---------- Contact ----------
const Contact = ({ t, waLink, telLink, lang }) => {
  const [form, setForm] = useState({ name: "", telephone: "", email: "", matter_type: "", message: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { firstName, matterType }
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success(t.contact.form.success);
      const firstName = (form.name || "").trim().split(/\s+/)[0] || "";
      const matterType = form.matter_type || "";
      setSubmitted({ firstName, matterType });
      setForm({ name: "", telephone: "", email: "", matter_type: "", message: "", company: "" });
    } catch (err) {
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  const buildFollowUpWa = () => {
    if (!submitted) return waLink;
    const fn = submitted.firstName;
    const mt = submitted.matterType;
    const msg = lang === "tr"
      ? `Merhaba, ben ${fn}. Web sitenizde form doldurdum.${mt ? ` İşlem türüm: ${mt}.` : ""}`
      : `Hello, my name is ${fn}. I have submitted an enquiry through your website.${mt ? ` My matter type is: ${mt}.` : ""}`;
    return `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <section id="contact" className="py-20 sm:py-24 lg:py-32 bg-white" data-testid="contact-section">
      <Container>
        <div className="max-w-2xl">
          <Overline>{t.contact.overline}</Overline>
          <h2 className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-navy">{t.contact.title}</h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600">{t.contact.subtitle}</p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-start">
          <div className="flex flex-col gap-6">
            <div className="bg-navy rounded-sm p-8 sm:p-10 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 h-1 w-24 bg-gold" />
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-light">{t.contact.whatsappLabel}</p>
              <a href={telLink} className="block mt-3 font-heading text-2xl sm:text-3xl font-semibold text-white hover:text-gold-light transition-colors">{BUSINESS.phone}</a>
              <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" onClick={() => trackWhatsApp("contact")}
                className="mt-6 w-full inline-flex items-center justify-center gap-2.5 bg-gold hover:bg-gold-dark text-navy text-base font-bold px-7 py-4 rounded-sm transition-colors">
                <MessageCircle className="w-5 h-5" strokeWidth={2} />
                {t.contact.whatsappCta}
              </a>
              <p data-testid="free-assessment-trust" className="mt-3 text-xs text-slate-300 leading-relaxed">
                <span className="font-semibold text-gold-light">{t.trust.assessmentTitle}</span>
                <span className="mx-1.5 text-slate-500">·</span>
                <span>{t.trust.assessmentSubtitle}</span>
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-white/10">
                <a href={telLink} data-testid="contact-call" className="flex items-center gap-3 group">
                  <span className="flex items-center justify-center w-9 h-9 rounded-sm bg-white/10"><Phone className="w-4 h-4 text-gold-light" strokeWidth={2} /></span>
                  <div><p className="text-[11px] uppercase tracking-wider text-slate-400">{t.contact.callLabel}</p><p className="text-sm font-medium text-white group-hover:text-gold-light transition-colors">{BUSINESS.phone}</p></div>
                </a>
                <a href={`mailto:${BUSINESS.email}`} data-testid="contact-email" className="flex items-center gap-3 group">
                  <span className="flex items-center justify-center w-9 h-9 rounded-sm bg-white/10"><Mail className="w-4 h-4 text-gold-light" strokeWidth={2} /></span>
                  <div className="min-w-0"><p className="text-[11px] uppercase tracking-wider text-slate-400">{t.contact.emailLabel}</p><p className="text-sm font-medium text-white group-hover:text-gold-light transition-colors truncate">{BUSINESS.email}</p></div>
                </a>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-sm p-6 flex items-start gap-3">
              <MapPin className="w-5 h-5 text-gold-dark mt-0.5 flex-shrink-0" strokeWidth={2} />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{t.contact.officeLabel}</p>
                <address className="mt-1 not-italic text-sm font-medium text-navy leading-relaxed">
                  {BUSINESS.address.line1}<br />{BUSINESS.address.line2}<br />
                  {BUSINESS.address.line3}, {BUSINESS.address.line4}<br />{BUSINESS.address.line5}
                </address>
              </div>
            </div>
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="bg-slate-50 border border-slate-200 rounded-sm p-7 sm:p-9">
            <h3 className="font-heading text-xl font-semibold text-navy">{t.contact.form.heading}</h3>
            {/* Honeypot — hidden from humans, bots fill it */}
            <input type="text" name="company" tabIndex="-1" autoComplete="off" value={form.company} onChange={update("company")}
              className="hidden" aria-hidden="true" data-testid="hp-company" />
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">{t.contact.form.name}</label>
                <input required data-testid="form-name" value={form.name} onChange={update("name")}
                  className="w-full border border-slate-300 rounded-sm px-4 py-3 text-base text-navy placeholder-slate-400 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors bg-white" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">{t.contact.form.telephone}</label>
                <input required data-testid="form-telephone" value={form.telephone} onChange={update("telephone")}
                  className="w-full border border-slate-300 rounded-sm px-4 py-3 text-base text-navy placeholder-slate-400 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors bg-white" />
              </div>
            </div>
            <div className="mt-5">
              <label className="block text-sm font-semibold text-navy mb-2">{t.contact.form.email}</label>
              <input required type="email" data-testid="form-email" value={form.email} onChange={update("email")}
                className="w-full border border-slate-300 rounded-sm px-4 py-3 text-base text-navy placeholder-slate-400 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors bg-white" />
            </div>
            <div className="mt-5">
              <label className="block text-sm font-semibold text-navy mb-2">{t.contact.form.matterType}</label>
              <select data-testid="form-matter-type" value={form.matter_type} onChange={update("matter_type")}
                className="w-full border border-slate-300 rounded-sm px-4 py-3 text-base text-navy focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors bg-white">
                <option value="">{t.contact.form.matterTypePlaceholder}</option>
                {t.contact.form.matterTypes.map((mt) => (
                  <option key={mt} value={mt}>{mt}</option>
                ))}
              </select>
            </div>
            <div className="mt-5">
              <label className="block text-sm font-semibold text-navy mb-2">{t.contact.form.message}</label>
              <textarea required rows={5} data-testid="form-message" value={form.message} onChange={update("message")}
                className="w-full border border-slate-300 rounded-sm px-4 py-3 text-base text-navy placeholder-slate-400 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors resize-none bg-white" />
            </div>
            <button type="submit" disabled={loading} data-testid="form-submit"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-950 disabled:opacity-60 text-white text-base font-semibold px-7 py-4 rounded-sm transition-colors">
              {loading ? t.contact.form.submitting : t.contact.form.submit}
              {!loading && <ArrowRight className="w-5 h-5 text-gold-light" strokeWidth={2} />}
            </button>
            {submitted && (
              <a
                href={buildFollowUpWa()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="form-continue-wa"
                onClick={() => trackWhatsApp("form-followup")}
                className="mt-4 w-full inline-flex items-center justify-center gap-2.5 bg-gold hover:bg-gold-dark text-navy text-base font-bold px-7 py-4 rounded-sm transition-colors"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={2} />
                {t.contact.form.continueWa}
              </a>
            )}
          </form>
        </div>
      </Container>
    </section>
  );
};

// ---------- Legal Modal ----------
const LegalModal = ({ doc, t, onClose }) => (
  <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="legal-modal">
      {doc && (
        <>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-navy">{doc.title}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">{doc.updated}</DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-5">
            {doc.sections.map((s, i) => (
              <div key={i}>
                <h4 className="font-heading text-base font-semibold text-navy">{s.h}</h4>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.p}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

// ---------- Footer ----------
const Footer = ({ t, telLink, waLink, openLegal }) => (
  <footer className="bg-navy-950 text-slate-400 py-16" data-testid="site-footer">
    <Container>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-10 h-10 bg-navy text-gold font-heading font-bold text-lg rounded-sm border border-gold/30">TD</span>
            <span className="font-heading text-[17px] font-semibold tracking-tight text-white leading-none">
              Turkish Docs<span className="block text-[11px] font-medium uppercase tracking-[0.2em] text-gold-light mt-0.5">London</span>
            </span>
          </div>
          <p className="mt-5 text-sm text-slate-400 max-w-sm">{t.footer.tagline}</p>
          <p className="mt-2 text-sm text-slate-500">{t.footer.location}</p>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-white">{t.footer.contactTitle}</p>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><a href={telLink} className="hover:text-gold-light transition-colors">{BUSINESS.phone}</a></li>
            <li><a href={waLink} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("footer")} className="hover:text-gold-light transition-colors">WhatsApp: {BUSINESS.phone}</a></li>
            <li><a href={`mailto:${BUSINESS.email}`} className="hover:text-gold-light transition-colors break-words">{BUSINESS.email}</a></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-white">{t.footer.legalTitle}</p>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><button data-testid="footer-privacy" onClick={() => openLegal("privacy")} className="hover:text-gold-light transition-colors">{t.footer.privacy}</button></li>
            <li><button data-testid="footer-terms" onClick={() => openLegal("terms")} className="hover:text-gold-light transition-colors">{t.footer.terms}</button></li>
            <li><button data-testid="footer-cookie" onClick={() => openLegal("cookie")} className="hover:text-gold-light transition-colors">{t.footer.cookie}</button></li>
          </ul>
        </div>
      </div>

      <div className="mt-12 pt-8 border-t border-white/10">
        <p className="text-xs text-slate-500 leading-relaxed max-w-3xl">{t.footer.disclaimer}</p>
        <p className="mt-6 text-xs text-slate-600">© {new Date().getFullYear()} Turkish Docs London. {t.footer.rights}</p>
      </div>
    </Container>
  </footer>
);

// ---------- App ----------
function App() {
  const [lang, setLang] = useState("tr");
  const [legal, setLegal] = useState(null);
  const t = content[lang];
  const waLink = makeWaLink(t.whatsappMessage || BUSINESS.whatsappMessage);
  const telLink = `tel:${BUSINESS.phoneRaw}`;

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <div className="App bg-white min-h-screen pb-[70px] sm:pb-0">
      <Header t={t} lang={lang} setLang={setLang} waLink={waLink} />
      <main>
        <Hero t={t} waLink={waLink} />
        <TrustBar t={t} />
        <Why t={t} waLink={waLink} />
        <Services t={t} />
        <Process t={t} />
        <Reviews t={t} />
        <SeoContent t={t} />
        <Hours t={t} waLink={waLink} />
        <Faq t={t} />
        <Contact t={t} waLink={waLink} telLink={telLink} lang={lang} />
      </main>
      <Footer t={t} telLink={telLink} waLink={waLink} openLegal={setLegal} />

      <LegalModal doc={legal ? LEGAL[legal] : null} t={t} onClose={() => setLegal(null)} />

      <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="floating-whatsapp" onClick={() => trackWhatsApp("sticky")}
        className="hidden sm:inline-flex fixed bottom-5 right-5 z-40 items-center gap-2.5 bg-navy hover:bg-navy-950 text-white pl-4 pr-5 py-3.5 rounded-full shadow-[0_10px_30px_rgba(11,31,58,0.35)] transition-colors border border-gold/40"
        aria-label="WhatsApp">
        <MessageCircle className="w-6 h-6 text-gold-light" strokeWidth={2} />
        <span className="text-sm font-semibold hidden sm:inline">WhatsApp</span>
      </a>

      <div className="sm:hidden fixed inset-x-0 bottom-0 z-50 grid grid-cols-2 bg-white border-t border-slate-200 shadow-[0_-6px_24px_rgba(11,31,58,0.12)] p-2 gap-2" data-testid="mobile-contact-bar">
        <a href={telLink} className="inline-flex items-center justify-center gap-2 bg-white border border-navy/20 text-navy font-bold py-3 rounded-sm">
          <Phone className="w-5 h-5" /> {t.mobileBar.call}
        </a>
        <a href={waLink} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("mobile_bottom")}
          className="inline-flex items-center justify-center gap-2 bg-[#1f9d55] text-white font-bold py-3 rounded-sm">
          <MessageCircle className="w-5 h-5" /> WhatsApp
        </a>
      </div>

      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
